import type { StateCreator } from "zustand";
import { actionNameBuilder } from "../../utilities/storeActionNameBuilder";
import { Entity } from "../../types";

type FilterType = "intersects" | "contains" | "equals";

interface Filter {
  type: FilterType;
  db_table: string;
  field: string;
  value: string | null;
}

export interface FilterState {
  filterArea: string | null;
  currentFilter: Filter;
  filterLayersVisible: boolean;
  selectedPrimaryFilterEntity: Entity | null;
}

export interface FilterActions {
  setFilterArea: (filterArea: FilterState["filterArea"]) => void;
  setCurrentFilter: (filter: Filter) => void;
  setFilterLayersVisible: (visible: boolean) => void;
  setSelectedPrimaryFilterEntity: (entity: Entity | null, showFilteredLayers?: boolean) => void;
  resetFilter: () => void;
}

export interface FilterSlice {
  filter: FilterState & {
    actions: FilterActions;
  };
}

const actionName = actionNameBuilder("filterSelection");

/**
 * Slice creator typed generically so it composes without TS errors in the combined store.
 */
export const createFilterSelectionSlice = <T extends FilterSlice>(
  ...[set]: Parameters<StateCreator<T, [["zustand/devtools", never], ["zustand/immer", never]], []>>
): FilterSlice => ({
  filter: {
    // Initial state
    filterArea: null,
    currentFilter: { type: "intersects", db_table: "FamilyArea", field: "name", value: "" },
    filterLayersVisible: false,
    selectedPrimaryFilterEntity: null,

    // Actions
    actions: {
      setFilterArea: (filterArea) =>
        set(
          (state) => {
            state.filter.filterArea = filterArea;
          },
          undefined,
          actionName("setFilterArea")
        ),
      setCurrentFilter: (filter) =>
        set(
          (state) => {
            state.filter.currentFilter = filter;
          },
          undefined,
          actionName("setCurrentFilter")
        ),
      setFilterLayersVisible: (visibility) =>
        set(
          (state) => {
            state.filter.filterLayersVisible = visibility;
          },
          undefined,
          actionName("setFilterLayersVisible")
        ),
      setSelectedPrimaryFilterEntity: (entity) =>
        set(
          (state) => {
            state.filter.selectedPrimaryFilterEntity = entity;
          },
          undefined,
          actionName("setSelectedPrimaryFilterEntity")
        ),
      resetFilter: () =>
        set(
          (state) => {
            state.filter.filterArea = null;
            state.filter.currentFilter = { type: "intersects", db_table: "FamilyArea", field: "name", value: "" };
            state.filter.filterLayersVisible = false;
            state.filter.selectedPrimaryFilterEntity = null;
          },
          undefined,
          actionName("resetFilter")
        )
    }
  }
});
